// projectDisplay — the shared display primitives for the platform-projects surface
// (promoted from the Workbench story's page-local blocks at their first real
// consumers: the list, peek, and detail). Everything the list row / peek / detail
// renders about a project's status, brand, add-ons, licensing, countries, and
// members lives here so the three surfaces can't drift. Pure logic (effectiveStatus)
// is unit-tested in projectDisplay.test.ts.
//
// Numbers + dates go through lib/format (ADR-030 locale-aware, Arabic-Indic digits);
// country names resolve via Intl.DisplayNames in the active language.

import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type {
  PlatformProjectListItem,
  ProjectStatus,
  ProjectType,
} from '@ems-app/schemas/projects/platform-responses';
import { Badge, type BadgeAppearance, type BadgeVariant } from '../ui/Badge.js';
import { Tooltip } from '../ui/Tooltip.js';
import { InfoPopover } from '../ui/InfoPopover.js';
import { icons } from '../ui/icons.js';
import { countryName, formatDate, formatNumber } from '../../lib/format.js';
import { SeatStat } from './SeatStat.js';
import { ModuleChips } from './ModuleChips.js';

// Every display helper reads only list-item fields; the detail object (a superset)
// is assignable, so both surfaces share these.
export type ProjectRow = PlatformProjectListItem;


// ── effective status (pure) ────────────────────────────────────────────────────
// The stored status widened with the trial verdict — trial is orthogonal to
// type/status: an active-status project can be a live or an expired trial. This is
// what every surface badges + the list filters on.
export type EffectiveStatus = 'active' | 'trial' | 'trial-expired' | 'suspended' | 'terminated';

/** The trial's end has passed — the one rule the badge and the Trial card read (no end set: never ended). */
export function trialEnded(p: Pick<ProjectRow, 'trial_expires_at'>, now: Date = new Date()): boolean {
  return p.trial_expires_at != null && new Date(p.trial_expires_at) < now;
}

export function effectiveStatus(
  p: Pick<ProjectRow, 'status' | 'is_trial' | 'trial_expires_at'>,
  now: Date = new Date(),
): EffectiveStatus {
  if (p.status === 'suspended') return 'suspended';
  if (p.status === 'terminated') return 'terminated';
  if (p.is_trial && p.trial_expires_at) return trialEnded(p, now) ? 'trial-expired' : 'trial';
  return 'active';
}

/** Days between an ISO instant and now, rounded (the trial countdown). */
export function daysUntil(iso: string, now: Date = new Date()): number {
  return Math.round((new Date(iso).getTime() - now.getTime()) / 86_400_000);
}

// The status pill is the LOCKED convention (COMPONENTS §Badge): calm `subtle`
// appearance; three semantic buckets — live=success · trial=info · every negative
// =warning. No invented colours, no dot.
const STATUS_VARIANT: Record<EffectiveStatus, BadgeVariant> = {
  active: 'success',
  trial: 'info',
  'trial-expired': 'warning',
  suspended: 'warning',
  terminated: 'warning',
};

export function StatusBadge({ status, appearance = 'subtle' }: { status: EffectiveStatus; appearance?: BadgeAppearance }) {
  const { t } = useTranslation('platform-projects');
  const label = t(`status.${status}`);
  return (
    <Badge variant={STATUS_VARIANT[status]} appearance={appearance} title={appearance === 'dot' ? label : undefined}>
      {label}
    </Badge>
  );
}

// The muted date line under the status pill (since / days-left / ended).
function statusDateLine(p: ProjectRow, status: EffectiveStatus, t: TFunction, zone: string): string | null {
  switch (status) {
    case 'active':
      return t('list.since', { date: formatDate(p.created_at, 'date', { timeZone: zone }) ?? '' });
    case 'trial':
      return p.trial_expires_at ? t('list.daysLeft', { count: daysUntil(p.trial_expires_at) }) : null;
    case 'trial-expired':
      return p.trial_expires_at ? t('list.ended', { date: formatDate(p.trial_expires_at, 'date', { timeZone: zone }) ?? '' }) : null;
    case 'suspended':
      return t('list.since', { date: formatDate(p.suspended_at ?? p.created_at, 'date', { timeZone: zone }) ?? '' });
    case 'terminated':
      return t('list.ended', { date: formatDate(p.terminated_at ?? p.created_at, 'date', { timeZone: zone }) ?? '' });
  }
}

// The list state cell: the status pill over a muted date line.
export function StatusCell({ project, zone }: { project: ProjectRow; /** The page's one zone — the viewer's own, hoisted (one `/me` observer per page, not per row). */ zone: string }) {
  const { t } = useTranslation('platform-projects');
  const status = effectiveStatus(project);
  const date = statusDateLine(project, status, t, zone);
  return (
    <div className="flex flex-col items-start gap-1">
      <StatusBadge status={status} />
      {date && <span className="text-[11px] text-[var(--color-text-secondary)] tabular-nums">{date}</span>}
    </div>
  );
}

// ── brand dot ───────────────────────────────────────────────────────────────
// A quiet brand accent — a small dot in the project's brand colour (DESIGN-TOKENS
// §Color: brand is a small accent, never dominates the row).
export function BrandDot({ project, size = 10 }: { project: Pick<ProjectRow, 'color_primary'>; size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block shrink-0 rounded-full border border-[var(--color-border)]"
      style={{ width: size, height: size, backgroundColor: project.color_primary }}
    />
  );
}

// ── add-ons ────────────────────────────────────────────────────────────────
// The three optional modules as a fixed set (stable width across rows). Field force
// = ticket; invoicing = receipt; vehicles = truck (icons by concept, ADR-042).
export const ADDON_DEFS = [
  { key: 'has_invoicing', labelKey: 'addons.invoicing', Icon: icons.invoicing },
  { key: 'has_field_force', labelKey: 'addons.fieldForce', Icon: icons.ticket },
  { key: 'has_vehicles', labelKey: 'addons.vehicles', Icon: icons.vehicle },
] as const;

// The list's fixed three-slot icon row — every project shows all three, enabled
// (brand-tint slot) or disabled (muted glyph).
export function AddonIcons({ project }: { project: ProjectRow }) {
  const { t } = useTranslation('platform-projects');
  return (
    <div className="flex items-center gap-1.5">
      {ADDON_DEFS.map(({ key, labelKey, Icon }) => {
        const on = project[key];
        const label = t(labelKey);
        return (
          <Tooltip key={key} content={`${label} — ${on ? t('addons.on') : t('addons.off')}`}>
            <span
              className={`inline-flex h-7 w-7 items-center justify-center rounded-md outline-none focus-ring ${
                on
                  ? 'bg-[var(--color-primary-display)]/12 text-[var(--color-primary-display)]'
                  : 'text-[var(--color-text-disabled)]'
              }`}
            >
              <Icon size={16} aria-hidden />
              <span className="sr-only">{`${label}: ${on ? t('addons.stateOn') : t('addons.stateOff')}`}</span>
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

// The peek/detail enabled-add-ons chips (presence = enabled; the on/off column is
// the list's job). None on → a muted note.
export function AddonChips({ project }: { project: ProjectRow }) {
  const { t } = useTranslation('platform-projects');
  return (
    <ModuleChips
      items={ADDON_DEFS.map(({ key, labelKey, Icon }) => ({
        key,
        label: t(labelKey),
        icon: Icon,
        on: project[key],
      }))}
      emptyLabel={t('addons.none')}
    />
  );
}

// ── members cell ──────────────────────────────────────────────────────────────
export function MembersCell({ project }: { project: ProjectRow }) {
  const { t } = useTranslation('platform-projects');
  return (
    <div className="flex flex-col gap-0.5">
      <span className="tabular-nums text-sm text-[var(--color-text-primary)]">{formatNumber(project.member_count)}</span>
      <span className="text-[11px] text-[var(--color-text-secondary)] tabular-nums">
        {t('list.activeWindow', { count: project.active_member_count_30d })}
      </span>
    </div>
  );
}

// ── countries cell ──────────────────────────────────────────────────────────
// 1–2 countries each on a line; 3+ → first name + "+N more" (the rest behind it, a tap away).
export function CountriesCell({ project }: { project: ProjectRow }) {
  const { t, i18n } = useTranslation('platform-projects');
  const list = project.countries;
  if (list.length === 0) return <span className="text-[var(--color-text-disabled)]">—</span>;
  if (list.length <= 2) {
    return (
      <div className="flex flex-col gap-0.5">
        {list.map((iso) => (
          <span key={iso} className="text-sm text-[var(--color-text-primary)]">
            {countryName(iso, i18n.language)}
          </span>
        ))}
      </div>
    );
  }
  const [first, ...rest] = list;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm text-[var(--color-text-primary)]">{countryName(first ?? '', i18n.language)}</span>
      <InfoPopover label={t('list.cols.countries')} trigger={t('list.countriesMore', { count: rest.length })} triggerClassName="w-fit rounded-sm text-[11px] text-[var(--color-text-secondary)] underline decoration-dotted decoration-from-font underline-offset-2">
        <ul className="space-y-0.5">
          {rest.map((c) => (
            <li key={c}>{countryName(c, i18n.language)}</li>
          ))}
        </ul>
      </InfoPopover>
    </div>
  );
}

// ── licensing / active sites ──────────────────────────────────────────────────
// Per-tier figures come straight off the item's `tiers` (the tiers it includes — the
// purchased cap + active usage; ADR-055). An esco tier carries no cap (`seats: null`,
// metered) — its sites read without a ceiling.
export interface TierFigure {
  label: string;
  active: number;
  cap: number | null;
}
export function activeSiteRows(p: ProjectRow): TierFigure[] {
  return p.tiers.map((s) => ({ label: s.tier_name, active: s.sites_held, cap: s.seats }));
}

// The compact list-cell breakdown — active/cap per purchased tier (text, no bars).
export function ActiveSites({ project }: { project: ProjectRow }) {
  const rows = activeSiteRows(project);
  if (rows.length === 0) return <span className="text-[var(--color-text-disabled)]">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-baseline gap-2 text-xs">
          <span className="w-16 shrink-0 text-[var(--color-text-secondary)]">{r.label}</span>
          <span className="tabular-nums font-medium text-[var(--color-text-primary)]">
            {formatNumber(r.active)}
            {r.cap != null && <span className="font-normal text-[var(--color-text-secondary)]">/{formatNumber(r.cap)}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

// One tier's licensing figure — now a thin i18n binding over the promoted
// `SeatStat` pattern (shared with the project-admin settings surface). The
// markup + the full-vs-headroom rule live in the pattern; this wrapper only
// supplies the two strings from THIS surface's namespace.
export function TierStat({ label, active, cap, dense }: TierFigure & { dense?: boolean }) {
  const { t } = useTranslation('platform-projects');
  return (
    <SeatStat
      label={label}
      active={active}
      cap={cap}
      dense={dense}
      fullLabel={t('licensing.full')}
      leftLabel={(n) => t('licensing.left', { count: n })}
    />
  );
}

// The commercial-model lead line + the per-tier figures. Shared by peek + detail.
export function LicensingSummary({ project, dense }: { project: ProjectRow; dense?: boolean }) {
  const { t } = useTranslation('platform-projects');
  const rows = activeSiteRows(project);
  const metered = project.type === 'esco';
  return (
    <div>
      <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
        <span className="font-semibold text-[var(--color-text-primary)]">
          {metered ? t('licensing.metered') : t('licensing.prepaid')}
        </span>
        {metered ? t('licensing.meteredLead') : t('licensing.prepaidLead')}
      </p>
      {rows.length === 0 ? (
        <span className="text-xs text-[var(--color-text-disabled)]">{t('licensing.noActiveSites')}</span>
      ) : (
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {rows.map((r) => (
            <TierStat key={r.label} label={r.label} active={r.active} cap={r.cap} dense={dense} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── type label hook ───────────────────────────────────────────────────────────
/** The localized project-type label ("ESCO" / "Embedded" / "Demo"). */
export function useTypeLabel(): (type: ProjectType) => string {
  const { t } = useTranslation('platform-projects');
  return useMemo(() => (type: ProjectType) => t(`type.${type}`), [t]);
}

// Re-export the status enum for consumers that filter on it.
export type { ProjectStatus };
